import round from "lodash.round";

/**
 * Minimal shape of a Stripe promotion code / coupon snapshot the discount math
 * needs. `Stripe.PromotionCode` and our stored `AppliedCoupon` both fit it.
 */
export interface CouponLike {
  coupon: {
    amount_off?: number | null;
    percent_off?: number | null;
    /** "once" | "repeating" | "forever" — missing is treated as "once". */
    duration?: string | null;
  };
}

/**
 * Whether a coupon keeps applying to the recurring installment cycles.
 * Only "forever"/"repeating" carry over; "once" (and unknown) apply to the
 * first payment only.
 */
export function couponAppliesToRecurring(coupon: CouponLike): boolean {
  const duration = coupon.coupon.duration;
  return Boolean(duration && duration !== "once");
}

/**
 * Applies a list of stacked coupons to a base amount (minor units, e.g. grosze).
 *
 * Coupons are applied sequentially — the same way Stripe evaluates multiple
 * `discounts` on an invoice — so the result matches what Stripe actually
 * charges on the card path. Percent coupons discount the running total; fixed
 * `amount_off` coupons subtract flat. The running total is clamped to 0 after
 * each step so an over-100% stack can never go negative.
 *
 * @param recurringOnly when true, only coupons that apply to recurring cycles
 *   are counted — used to estimate the 2nd..Nth installment amount.
 */
export function applyCouponsToAmount(
  baseAmount: number,
  coupons: CouponLike[],
  options: { recurringOnly?: boolean } = {},
): number {
  const { recurringOnly = false } = options;

  let amount = baseAmount;

  for (const promotionCode of coupons) {
    if (recurringOnly && !couponAppliesToRecurring(promotionCode)) {
      continue;
    }

    const { amount_off, percent_off } = promotionCode.coupon;

    if (amount_off) {
      amount -= amount_off;
    } else if (percent_off) {
      amount -= amount * (percent_off / 100);
    }

    amount = Math.max(0, amount);
  }

  return round(amount);
}
